import {
  TX_STATUS_COMPLETED,
  TX_STATUS_CUSTOMER_REVIEW,
  TX_STATUS_FAILED,
  TX_STATUS_HOLD,
  TX_STATUS_NEW,
  TX_STATUS_POST_PROCESSING,
  TX_STATUS_PROCESSING,
  TX_STATUS_RESTARTED,
} from '../config'

// Dashboard display copy for transaction states and hold reasons.
//
// SCOPE: transaction state only. `state` is an overloaded field name in this
// codebase — identity documents, transaction callbacks and postal addresses
// each carry one with an unrelated vocabulary. Do not apply these maps to them.
//
// Hold reasons are only the ones the backend deliberately classifies. Anything
// unrecognised renders plain HOLD by design, not by omission: exhaustively
// categorising the AML providers' vocabularies buys little beyond these six.
// See the Minerva path section of the payload contract design note.

export const HOLD_REASON_SANCTIONS_WATCHLIST = 'sanctions_watchlist'
export const HOLD_REASON_PEP_ADVERSE_MEDIA = 'pep_adverse_media'
export const HOLD_REASON_FACE_MATCH = 'face_match'
export const HOLD_REASON_LIVENESS = 'liveness'
export const HOLD_REASON_COUNTRY_MISMATCH = 'country_mismatch'
export const HOLD_REASON_RNPN_MISMATCH = 'rnpn_mismatch'

export const GENERIC_HOLD_LABEL = 'HOLD'

export const TRANSACTION_STATE_LABELS = {
  [TX_STATUS_NEW]: 'New',
  [TX_STATUS_PROCESSING]: 'Processing',
  [TX_STATUS_COMPLETED]: 'Approved',
  [TX_STATUS_FAILED]: 'Declined',
  [TX_STATUS_HOLD]: GENERIC_HOLD_LABEL,
  [TX_STATUS_POST_PROCESSING]: 'Netki Review',
  [TX_STATUS_CUSTOMER_REVIEW]: 'Customer Review',
  [TX_STATUS_RESTARTED]: 'Restarted',
}

export const HOLD_REASON_LABELS = {
  [HOLD_REASON_SANCTIONS_WATCHLIST]: 'HOLD - Sanctions/Watchlist',
  [HOLD_REASON_PEP_ADVERSE_MEDIA]: 'HOLD - PEP/Adverse Media',
  [HOLD_REASON_FACE_MATCH]: 'HOLD - Face Match',
  [HOLD_REASON_LIVENESS]: 'HOLD - Liveness Below Threshold',
  [HOLD_REASON_COUNTRY_MISMATCH]: 'HOLD - Country Mismatch',
  [HOLD_REASON_RNPN_MISMATCH]: 'HOLD - RNPN/Front Image Mismatch',
}

// IdentityDocument.state — a different FSM from the transaction one, with its
// own eight-value vocabulary. `completed`/`failed` render as Approved/Declined
// deliberately: that wording is what reviewers read on the detail page today.
export const DOCUMENT_STATE_LABELS = {
  new: 'New',
  pending: 'Pending',
  processing: 'Processing',
  completed: 'Approved',
  failed: 'Declined',
  disabled: 'Disabled',
  quarantined: 'Quarantined',
  fraud: 'Fraud',
}

// Careful: `uploaded` is a value in BOTH accredited_document_status
// (uploaded/verified/invalid, on IdentityAccreditedInvestorDocument) and
// accredited_investor_status (open/uploaded/hold/expired/rejected/accepted, on
// IdentityAccreditedInvestorStatus). Same string, different meanings, different
// tables. Do not merge those two into one map.

const warned = new Set()

const warnUnmapped = (kind, value) => {
  if (process.env.NODE_ENV === 'production') {
    return
  }
  const key = `${kind}:${value}`
  if (warned.has(key)) {
    return
  }
  warned.add(key)
  // eslint-disable-next-line no-console
  console.warn(
    `display_names: unmapped ${kind} "${value}" — add it to the map rather than letting it render unlabelled.`
  )
}

export const resetUnmappedWarnings = () => warned.clear()

export const transactionStateLabel = (state) => {
  if (!state) {
    return state
  }
  if (Object.prototype.hasOwnProperty.call(TRANSACTION_STATE_LABELS, state)) {
    return TRANSACTION_STATE_LABELS[state]
  }
  warnUnmapped('transaction state', state)
  return state
}

// Unrecognised reasons fall back to the generic hold label rather than the raw
// key: an unclassified hold is still a hold, and a compliance reviewer should
// never see an enum key. The dev warning is the signal that one is missing.
export const holdReasonLabel = (holdReason) => {
  if (!holdReason) {
    return GENERIC_HOLD_LABEL
  }
  if (Object.prototype.hasOwnProperty.call(HOLD_REASON_LABELS, holdReason)) {
    return HOLD_REASON_LABELS[holdReason]
  }
  warnUnmapped('hold reason', holdReason)
  return GENERIC_HOLD_LABEL
}

// Returns '' rather than the falsy input, because the document rows uppercase
// the result directly and a null state would otherwise throw.
export const documentStateLabel = (state) => {
  if (!state) {
    return ''
  }
  if (Object.prototype.hasOwnProperty.call(DOCUMENT_STATE_LABELS, state)) {
    return DOCUMENT_STATE_LABELS[state]
  }
  warnUnmapped('document state', state)
  return state
}
